import _ from "lodash";
import * as Team from "src/state/modules/team/types";
import * as Eden from "src/eden/content";
import { Season } from "src/state/modules/team/types";

type HasSeasonInfo = {
  seasonName: Team.Season;
  seasonYear: number;
};
const SeasonSortOrder: { [key in Season]: number } = {
  [Season.WINTER]: 0,
  [Season.FALL]: 1,
  [Season.SUMMER]: 2,
  [Season.SPRING]: 3,
};

export const compareSeasons = (a: Season, b: Season) => SeasonSortOrder[a] - SeasonSortOrder[b];

const sortSeasonAndYear = (a: HasSeasonInfo, b: HasSeasonInfo) => {
  return a.seasonYear === b.seasonYear
    ? compareSeasons(a.seasonName, b.seasonName)
    : a.seasonYear > b.seasonYear
    ? -1
    : 1;
};

const winterSeasonDisplay = (season: Season, year: number, fullYear: boolean) => {
  const secondYear = fullYear ? `${year + 1}` : `${(year + 1).toString().slice(2)}`;
  return `${season} ${year}-${secondYear}`;
};

const nonWinterSeasonDisplay = (season: Season, year: number) => `${season} ${year}`;

export const buildDisplaySeason = (season: Season, year: number, fullYear = false): string => {
  return _.capitalize(
    season === Season.WINTER ? winterSeasonDisplay(season, year, fullYear) : nonWinterSeasonDisplay(season, year)
  );
};

const convertApiTeamToListTeam = (team: Eden.Team): Team.ListTeam => {
  const seasonName = team.season_name as Team.Season;
  const seasonYear = team.season_year;
  const accessLevel = team.stat_access_level == "fans" ? "(open)" 
  : team.stat_access_level == "confirmed_individual" ? "(private - individual)" 
  : team.stat_access_level == "confirmed_full" ? "(private - full)" : "(unknown - reimmport team)";

  return {
    id: Team.toTeamID(team.id),
    name: team.name,
    sport: Team.Sport.BASEBALL,
    seasonName,
    seasonYear,
    statLevel: accessLevel,
    displaySeason: buildDisplaySeason(seasonName, seasonYear),
    playerCount: team.team_player_count ?? 0,
    seasonRecord: team.record,
    lastUpdated: team.last_updated,
  };
};

const groupTeamsBySeason = (listTeams: Team.ListTeam[]): Team.TeamListGroupedBySeason[] => {
  const grouped = _.groupBy(listTeams, (listTeam: Team.ListTeam) => listTeam.displaySeason);
  return Object.keys(grouped).map((x) => ({ [x]: grouped[x] })) as Team.TeamListGroupedBySeason[];
};

const sortEachSeasonByStaffAndAlpha = (teams: Team.TeamListGroupedBySeason[]): Team.TeamListGroupedBySeason[] => {
  const sortedTeams: Team.TeamListGroupedBySeason[] = teams.map((season) => {
    return { [Object.keys(season)[0]]: [] };
  });

  teams.forEach((season: Team.TeamListGroupedBySeason, index: number) => {
    const teams = _.flatten(Object.values(season));
    const splitByStaffStatus = _.partition(teams, true);
    const groupsSortedByName = _.flatMap(splitByStaffStatus, (ts) => _.sortBy(ts, "name"));
    sortedTeams[index][Object.keys(season)[0]] = groupsSortedByName as Team.ListTeam[];
  });
  return sortedTeams;
};

export const mapTeamsAndOrgsToGroupedBySeason = (apiTeams: Eden.Team[]): Team.TeamListGroupedBySeason[] => {
  const combined = _.map(apiTeams, convertApiTeamToListTeam);
  const convertedTeamsList = groupTeamsBySeason(combined.sort(sortSeasonAndYear));
  const sortedTeams = sortEachSeasonByStaffAndAlpha(convertedTeamsList);
  return sortedTeams;
};

export const mapEdenTeamToTeam = (apiTeam: Eden.Team): Team.Team => {
  return {
    id: Team.toTeamID(apiTeam.id),
    name: apiTeam.name,
    sport: Team.Sport.BASEBALL,
    city: apiTeam.city,
    state: apiTeam.state,
    country: apiTeam.country,
    ageGroup: apiTeam.age_group as Team.AgeGroup,
    seasonName: apiTeam.season_name as Team.Season,
    seasonYear: apiTeam.season_year,
    seasonRecord: `${apiTeam.record.wins}-${apiTeam.record.losses}-${apiTeam.record.ties}`,
    statAccessLevel: apiTeam.stat_access_level,
    scorekeepingAccessLevel: apiTeam.scorekeeping_access_level,
    streamingAccessLevel: apiTeam.streaming_access_level,
    competitionLevel: apiTeam.competition_level as Team.CompetitionLevel,
    teamType: apiTeam.team_type as Team.TeamType,
    inningsPerGame: apiTeam.settings?.scorekeeping?.bats?.innings_per_game,
    staff: apiTeam.staff,
    lastUpdated: apiTeam.last_updated,
  };
};
